import { useCallback, useRef } from 'react';

import { useNavigate } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { DOMAIN_HIERARCHY_LIMIT, NOTIFICATION_MESSAGES, ROUTES } from '@/shared/lib/constants';
import { getParentFqn } from '@/shared/lib/format/fqn';
import { assetKeys, discoveryKeys, domainKeys } from '@/shared/lib/query/queryKeys';

import { deleteDomainApi } from '@/features/domain/api';
import type { DeleteDomainRequest } from '@/features/domain/api';

type DeleteDomainContext = {
  domainFqn?: string;
  parentDomainFqn?: string;
};

type UseDeleteDomainOptions = {
  onTreeRefresh?: (parentFqn: string) => void;
  onDeleteStart?: (parentFqn: string) => void;
};

export const useDeleteDomain = (domainFqn?: string, options?: UseDeleteDomainOptions) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const parentDomainFqn = getParentFqn(domainFqn);

  const mutation = useMutation({
    mutationFn: (request: DeleteDomainRequest) => deleteDomainApi(request),
    onMutate: async (): Promise<DeleteDomainContext> => {
      if (domainFqn) {
        await queryClient.cancelQueries({ queryKey: domainKeys.detail(domainFqn) });
        queryClient.removeQueries({ queryKey: domainKeys.detail(domainFqn) });
      }

      return { domainFqn, parentDomainFqn };
    },
    onSuccess: (_data, _variables, context) => {
      const parentFqn = context?.parentDomainFqn || '';

      optionsRef.current?.onDeleteStart?.(parentFqn);

      if (parentFqn) {
        navigate(`${ROUTES.DOMAIN}/${encodeURIComponent(parentFqn)}/sub-domains`, { replace: true });
      } else {
        navigate(ROUTES.DOMAIN, { replace: true });
      }

      optionsRef.current?.onTreeRefresh?.(parentFqn);

      queryClient.invalidateQueries({ queryKey: domainKeys.hierarchy(DOMAIN_HIERARCHY_LIMIT) });
      queryClient.invalidateQueries({ queryKey: domainKeys.domainTree });
      queryClient.invalidateQueries({ queryKey: domainKeys.subDomainsPrefix() });

      if (parentFqn) {
        queryClient.invalidateQueries({ queryKey: domainKeys.detail(parentFqn) });
      }

      queryClient.invalidateQueries({ queryKey: assetKeys.assetBaseInfoPrefix() });
      queryClient.invalidateQueries({ queryKey: assetKeys.dataAssetsPrefix() });
      queryClient.invalidateQueries({ queryKey: assetKeys.schemaPrefix() });
      queryClient.invalidateQueries({ queryKey: assetKeys.lineagePrefix() });
      queryClient.invalidateQueries({ queryKey: discoveryKeys.searchPrefix() });

      notification.success({
        message: NOTIFICATION_MESSAGES.DOMAIN_DELETE_SUCCESS,
        description: NOTIFICATION_MESSAGES.DOMAIN_DELETE_SUCCESS_DESC,
        placement: 'bottomLeft',
        duration: 3,
      });
    },
  });

  const handleDelete = useCallback(
    (domainId: string, options?: { recursive?: boolean; hardDelete?: boolean }) => {
      return mutation.mutateAsync({
        domainId,
        recursive: options?.recursive ?? true,
        hardDelete: options?.hardDelete ?? true,
      });
    },
    [mutation]
  );

  return {
    ...mutation,
    handleDelete,
  };
};
